import { useState } from "react";
import { formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { StatCard } from "@/components/StatCard";
import { SupportConsolePageHeader } from "@/components/SupportConsoleLayout";
import { useAgentQueue, useAgentStats } from "@/lib/support";
import { TicketStatusPill } from "@/modules/support/ticketStatus";

type Scope = "open" | "mine" | "unassigned" | "all";
const SCOPES: Scope[] = ["open", "mine", "unassigned", "all"];

export default function SupportQueuePage() {
  const { t } = useTranslation();
  const { data: stats } = useAgentStats();
  const [scope, setScope] = useState<Scope>("open");

  const params: Record<string, string> = {};
  if (scope === "open") params.open = "1";
  else if (scope === "mine") params.assignee = "me";
  else if (scope === "unassigned") params.assignee = "unassigned";
  const { data: tickets, isLoading } = useAgentQueue(params);

  return (
    <>
      <SupportConsolePageHeader
        title={t("support.console.queue.title")}
        description={t("support.console.queue.subtitle")}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label={t("support.console.stats.open")} value={stats?.open ?? "—"} tone="info" />
        <StatCard label={t("support.console.stats.unassigned")} value={stats?.unassigned ?? "—"} tone="warn" />
        <StatCard label={t("support.console.stats.waitingCustomer")} value={stats?.waiting_customer ?? "—"} tone="info" />
        <StatCard label={t("support.console.stats.overdue")} value={stats?.overdue ?? "—"} tone="rose" />
        <StatCard label={t("support.console.stats.mine")} value={stats?.mine ?? "—"} tone="info" />
        <StatCard label={t("support.console.stats.resolved")} value={stats?.resolved ?? "—"} tone="ok" />
      </div>

      <div className="mb-4 flex gap-2">
        {SCOPES.map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={
              "rounded-md px-3 py-1 text-sm " +
              (scope === s
                ? "bg-nb-mint/15 text-nb-mint"
                : "text-nb-text-2 hover:bg-nb-surface/40")
            }
          >
            {t(`support.console.queue.scope.${s}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-nb-text-2">{t("common.loading")}</p>
      ) : !tickets?.length ? (
        <p className="text-nb-text-2">{t("support.console.queue.empty")}</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-nb-border">
          <table className="w-full text-sm">
            <thead className="bg-nb-surface/40 text-left text-xs text-nb-text-2">
              <tr>
                <th className="px-3 py-2">{t("support.console.queue.col.subject")}</th>
                <th className="px-3 py-2">{t("support.console.queue.col.org")}</th>
                <th className="px-3 py-2">{t("support.console.queue.col.plan")}</th>
                <th className="px-3 py-2">{t("support.console.queue.col.status")}</th>
                <th className="px-3 py-2">{t("support.console.queue.col.slaDue")}</th>
                <th className="px-3 py-2">{t("support.console.queue.col.assignee")}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((tk) => {
                const overdue =
                  tk.first_response_due_at &&
                  !tk.first_responded_at &&
                  new Date(tk.first_response_due_at) < new Date();
                return (
                  <tr key={tk.id} className="border-t border-nb-border hover:bg-nb-surface/30">
                    <td className="px-3 py-2">
                      <Link className="text-nb-text hover:text-cvp-accent"
                        to={`/support-console/tickets/${tk.id}`}>
                        {tk.subject}
                      </Link>
                      {(tk.requester_name || tk.requester_email) && (
                        <div className="text-xs text-nb-text-2">
                          {tk.requester_name
                            ? `${tk.requester_name} · ${tk.requester_email}`
                            : tk.requester_email}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-nb-text-2">{tk.org_name}</td>
                    <td className="px-3 py-2 text-nb-text-2">{tk.support_plan_slug}</td>
                    <td className="px-3 py-2"><TicketStatusPill status={tk.status} /></td>
                    <td className={"px-3 py-2 " + (overdue ? "text-cvp-danger" : "text-nb-text-2")}>
                      {tk.first_response_due_at
                        ? formatDateTime(tk.first_response_due_at)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-nb-text-2">{tk.assignee_email ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
